import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CaretRight } from "@phosphor-icons/react";

const TYPES = ["LOBBY", "SECTOR", "BLOCK", "BUILDING", "FLAT"];
const LABELS = {
  LOBBY: "Lobby",
  SECTOR: "Sector",
  BLOCK: "Block",
  BUILDING: "Building",
  FLAT: "Flat",
};

export default function LocationPicker({ onChange, selected = [] }) {
  // selected: array of fully chosen FLAT-or-deeper location ids
  const [allLocs, setAllLocs] = useState([]);
  const [picks, setPicks] = useState({ LOBBY: "", SECTOR: "", BLOCK: "", BUILDING: "", FLAT: "" });

  useEffect(() => {
    api.get("/locations").then((r) => setAllLocs(r.data));
  }, []);

  const byType = (type, parentId) =>
    allLocs.filter(
      (l) => l.type === type && (parentId === undefined ? true : l.parent_id === parentId)
    );

  const set = (type, val) => {
    const idx = TYPES.indexOf(type);
    const newPicks = { ...picks, [type]: val };
    // clear deeper picks
    for (let i = idx + 1; i < TYPES.length; i++) newPicks[TYPES[i]] = "";
    setPicks(newPicks);
  };

  const addLocation = () => {
    // pick the deepest non-empty pick
    let chosen = "";
    for (let i = TYPES.length - 1; i >= 0; i--) {
      if (picks[TYPES[i]]) {
        chosen = picks[TYPES[i]];
        break;
      }
    }
    if (!chosen) return;
    if (selected.includes(chosen)) return;
    onChange([...selected, chosen]);
    setPicks({ LOBBY: "", SECTOR: "", BLOCK: "", BUILDING: "", FLAT: "" });
  };

  const removeLocation = (id) => onChange(selected.filter((s) => s !== id));

  const locById = Object.fromEntries(allLocs.map((l) => [l.id, l]));
  const pathOf = (id) => {
    const chain = [];
    let cur = locById[id];
    let g = 0;
    while (cur && g < 10) {
      chain.unshift(`${LABELS[cur.type]} · ${cur.name}`);
      cur = cur.parent_id ? locById[cur.parent_id] : null;
      g++;
    }
    return chain.join(" / ");
  };

  return (
    <div className="space-y-4" data-testid="location-picker">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        {TYPES.map((type, i) => {
          const parentId = i === 0 ? null : picks[TYPES[i - 1]] || "__none__";
          const opts = byType(type, parentId === "__none__" ? "__nope__" : parentId);
          const disabled = i > 0 && !picks[TYPES[i - 1]];
          return (
            <div key={type}>
              <label className="mono-label block mb-1.5">
                {i + 1}. {LABELS[type]}
              </label>
              <select
                data-testid={`location-${type.toLowerCase()}-select`}
                disabled={disabled}
                value={picks[type]}
                onChange={(e) => set(type, e.target.value)}
                className="w-full border border-slate-300 px-3 py-2.5 bg-white text-sm text-slate-900 disabled:bg-slate-50 disabled:text-slate-400 outline-none focus:border-slate-900 transition-all font-mono"
                style={{ borderRadius: 2 }}
              >
                <option value="">— select —</option>
                {opts.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        data-testid="add-location-btn"
        onClick={addLocation}
        disabled={!Object.values(picks).some(Boolean)}
        className="btn-sharp btn-secondary text-sm disabled:opacity-50"
      >
        Pin to this address <CaretRight size={14} />
      </button>

      {selected.length > 0 && (
        <div className="space-y-2">
          <div className="mono-label">Selected addresses ({selected.length})</div>
          {selected.map((id) => (
            <div
              key={id}
              className="flex items-center justify-between border border-slate-200 px-3 py-2 bg-slate-50 font-mono text-sm"
              data-testid={`selected-location-${id}`}
            >
              <span className="text-slate-900 truncate">{pathOf(id)}</span>
              <button
                onClick={() => removeLocation(id)}
                className="text-orange-600 hover:text-orange-700 mono-label"
                data-testid={`remove-location-${id}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
